import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Spinner from '../../components/Spinner/Spinner';
import {
  getSsoSettings,
  ISsoSettings,
  resetSsoTenant,
  updateSsoEnabled,
  validateAndSetTenant,
} from '../../services/auth';
import { SpinnerCont } from '../SurveyContents/SurveyContents.styles';
import {
  ButtonRow,
  DangerButton,
  Divider,
  ErrorMessage,
  FieldGroup,
  FieldHelp,
  FieldLabel,
  GhostButton,
  InputRow,
  OrgSettingsContainer,
  PageSubtitle,
  PageTitle,
  PrimaryButton,
  SectionTitle,
  StepDescription,
  StepHeader,
  StepIconWrapper,
  StepSubtitle,
  StepTitle,
  StepTitleGroup,
  TenantInput,
  TenantValue,
  TenantValueBlock,
  TenantValueLabel,
  WizardCard,
} from './OrgSettings.styles';

type WizardStep = 'configure' | 'ready' | 'active';

const deriveStep = (s: ISsoSettings): WizardStep => {
  if (s.sso_enabled) return 'active';
  if (s.tenant_configured) return 'ready';
  return 'configure';
};

const OrgSettings: FC = () => {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<ISsoSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [tenantInput, setTenantInput] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await getSsoSettings();
        setSettings(data);
      } catch {
        setLoadError(t('orgSettings.sso.loadError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  /**
   * Wraps an API call: sets busy, clears previous error, updates settings on
   * success, re-throws on failure so callers can set a specific error message.
   */
  const withBusy = async (fn: () => Promise<ISsoSettings>): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      const updated = await fn();
      setSettings(updated);
    } catch (err: any) {
      const apiMsg: string | undefined = err?.response?.data?.error;
      throw apiMsg ? new Error(apiMsg) : err;
    } finally {
      setBusy(false);
    }
  };

  const handleValidate = async () => {
    const id = tenantInput.trim();
    if (!id) return;
    try {
      await withBusy(() => validateAndSetTenant(id));
    } catch (err: any) {
      setActionError(err?.message ?? t('orgSettings.sso.configure.validateError'));
    }
  };

  const handleEnable = async () => {
    try {
      await withBusy(() => updateSsoEnabled(true));
    } catch (err: any) {
      setActionError(err?.message ?? t('orgSettings.sso.enableError'));
    }
  };

  const handleDisable = async () => {
    try {
      await withBusy(() => updateSsoEnabled(false));
    } catch (err: any) {
      setActionError(err?.message ?? t('orgSettings.sso.disableError'));
    }
  };

  const handleReset = async () => {
    try {
      await withBusy(() => resetSsoTenant());
      setTenantInput('');
    } catch (err: any) {
      setActionError(err?.message ?? t('orgSettings.sso.resetError'));
    }
  };

  if (loading) {
    return (
      <OrgSettingsContainer>
        <SpinnerCont>
          <Spinner />
        </SpinnerCont>
      </OrgSettingsContainer>
    );
  }

  if (loadError) {
    return (
      <OrgSettingsContainer>
        <ErrorMessage>{loadError}</ErrorMessage>
      </OrgSettingsContainer>
    );
  }

  const step = deriveStep(settings!);

  return (
    <OrgSettingsContainer>
      <PageTitle>{t('orgSettings.title')}</PageTitle>
      <PageSubtitle>{t('orgSettings.sso.intro')}</PageSubtitle>

      <SectionTitle>{t('orgSettings.sso.sectionTitle')}</SectionTitle>

      <WizardCard>
        {/* ── Step 1: Configure ──────────────────────────────────── */}
        {step === 'configure' && (
          <>
            <StepHeader>
              <StepIconWrapper variant="neutral">⚙</StepIconWrapper>
              <StepTitleGroup>
                <StepTitle>{t('orgSettings.sso.configure.title')}</StepTitle>
                <StepSubtitle>{t('orgSettings.sso.configure.step')}</StepSubtitle>
              </StepTitleGroup>
            </StepHeader>

            <Divider />

            <FieldGroup>
              <FieldLabel htmlFor="tenant-input">
                {t('orgSettings.sso.configure.tenantLabel')}
              </FieldLabel>
              <InputRow>
                <TenantInput
                  id="tenant-input"
                  type="text"
                  value={tenantInput}
                  onChange={(e) => setTenantInput(e.target.value)}
                  placeholder={t('orgSettings.sso.configure.tenantPlaceholder')}
                  onKeyDown={(e) => e.key === 'Enter' && handleValidate()}
                  disabled={busy}
                />
                <PrimaryButton
                  onClick={handleValidate}
                  disabled={busy || !tenantInput.trim()}
                >
                  {busy
                    ? t('orgSettings.sso.configure.validating')
                    : t('orgSettings.sso.configure.validateButton')}
                </PrimaryButton>
              </InputRow>
              <FieldHelp>{t('orgSettings.sso.configure.tenantHelp')}</FieldHelp>
            </FieldGroup>

            {actionError && <ErrorMessage>{actionError}</ErrorMessage>}
          </>
        )}

        {/* ── Step 2: Tenant validated, ready to enable ──────────── */}
        {step === 'ready' && (
          <>
            <StepHeader>
              <StepIconWrapper variant="success">✓</StepIconWrapper>
              <StepTitleGroup>
                <StepTitle>{t('orgSettings.sso.ready.title')}</StepTitle>
                <StepSubtitle>{t('orgSettings.sso.ready.step')}</StepSubtitle>
              </StepTitleGroup>
            </StepHeader>

            <StepDescription>{t('orgSettings.sso.ready.description')}</StepDescription>

            <Divider />

            <TenantValueBlock>
              <TenantValueLabel>{t('orgSettings.sso.ready.connectedTenant')}</TenantValueLabel>
              <TenantValue>{settings?.entra_id_tenant}</TenantValue>
            </TenantValueBlock>

            {actionError && <ErrorMessage>{actionError}</ErrorMessage>}

            <ButtonRow>
              <PrimaryButton onClick={handleEnable} disabled={busy}>
                {t('orgSettings.sso.ready.enableButton')}
              </PrimaryButton>
              <GhostButton onClick={handleReset} disabled={busy}>
                {t('orgSettings.sso.ready.startOver')}
              </GhostButton>
            </ButtonRow>
          </>
        )}

        {/* ── Step 3: SSO active ─────────────────────────────────── */}
        {step === 'active' && (
          <>
            <StepHeader>
              <StepIconWrapper variant="active">●</StepIconWrapper>
              <StepTitleGroup>
                <StepTitle>{t('orgSettings.sso.active.title')}</StepTitle>
              </StepTitleGroup>
            </StepHeader>

            <StepDescription>{t('orgSettings.sso.active.description')}</StepDescription>

            <Divider />

            <TenantValueBlock>
              <TenantValueLabel>{t('orgSettings.sso.active.connectedTenant')}</TenantValueLabel>
              <TenantValue>{settings?.entra_id_tenant}</TenantValue>
            </TenantValueBlock>

            {actionError && <ErrorMessage>{actionError}</ErrorMessage>}

            <ButtonRow>
              <DangerButton onClick={handleDisable} disabled={busy}>
                {t('orgSettings.sso.active.disableButton')}
              </DangerButton>
              <GhostButton onClick={handleReset} disabled={busy}>
                {t('orgSettings.sso.active.reconfigureButton')}
              </GhostButton>
            </ButtonRow>
          </>
        )}
      </WizardCard>
    </OrgSettingsContainer>
  );
};

export default OrgSettings;

